import React, {useCallback, useEffect, useState} from 'react';

import {getUniqueId} from 'utils/id-util';
import {getChartWidth} from 'utils/layout-util';
import {isValid} from 'validators/number-validator';
import {OrientationEnum} from 'common/proxies/enum-proxy';

const defaultWindowState = {
  count: 0,
  height: 0,
  width: 0,
};

export const useChartId = () => {
  const [chartId, setChartId] = useState();
  useEffect(() => {
    setChartId(getUniqueId());
  }, []);
  return chartId;
};

export const useChartInteraction = (config, containerRef, callback) => {
  const defaultState = {
    activeIndex: -1,
    closestIndex: -1,
    scrollTop: 0,
    x: -1,
    y: -1,
  };
  const configuration = {
    direction: OrientationEnum.HORIZONTAL.id,
    height: 0,
    tickRange: 0,
    width: 0,
    x: 0,
    y: 0,
    ...config,
  };

  const [interaction, setInteraction] = useState(defaultState);
  const onInteraction = useCallback(
    (event, clear) => {
      setInteraction((oldInteraction) => {
        const currentInteraction = clear
          ? {
              ...defaultState,
              scrollTop: oldInteraction.scrollTop,
            }
          : {
              ...oldInteraction,
              ...event,
            };
        if (typeof callback === 'function') {
          setTimeout(() => {
            callback(currentInteraction);
          });
        }
        return currentInteraction;
      });
    },
    [interaction],
  );

  const events = {
    move: (event) => onInteraction(event),
    leave: (event) => onInteraction(event, true),
  };

  const onScroll = useCallback(() => {
    const currentScrollTop = containerRef.current.scrollTop;
    const y = interaction.y + (currentScrollTop - interaction.scrollTop);
    const activeIndex = Math.floor(y / configuration.tickRange);
    onInteraction({
      activeIndex,
      scrollTop: currentScrollTop,
      y,
    });
  }, [interaction]);

  useEffect(() => {
    if (containerRef.current && containerRef.current.addEventListener) {
      containerRef.current.addEventListener('scroll', onScroll);
      return () => {
        containerRef.current?.removeEventListener('scroll', onScroll);
      };
    }
    return () => null;
  }, [
    containerRef.current,
    interaction.activeIndex,
    interaction.y,
    interaction.scrollTop,
    configuration.tickRange,
  ]);

  return [interaction, events, configuration];
};

export const useChartLegend = (rowHeight = 0) => {
  const [height, setHeight] = useState(0);
  const setRowCount = useCallback(
    (rowsCount) => {
      const newHeight = isValid(rowsCount)
        ? Math.round(rowHeight * rowsCount)
        : 0;
      setHeight(newHeight);
    },
    [height],
  );
  return [height, setRowCount];
};

export const useDynamicChartWidth = (chartRef, callback) => {
  const [width, setWidth] = useState(0);
  const getCurrentWidth = useCallback(
    () => (chartRef.current ? getChartWidth(0, chartRef.current) : 0),
    [chartRef.current],
  );
  useEffect(() => {
    const chartWidth = getCurrentWidth();
    if (width !== chartWidth) {
      if (typeof callback === 'function') {
        setTimeout(() => {
          callback(chartWidth);
        });
      }
      setWidth(chartWidth);
    }
  });
  return width;
};

export const useWindowResize = () => {
  const [windowState, setWindowState] = useState(defaultWindowState);
  const handleResize = ({target}) => {
    setWindowState({
      ...windowState,
      width: target.innerWidth,
      height: target.innerHeight,
    });
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [windowState.height, windowState.width]);
  return [windowState, handleResize];
};
